/* eslint-disable */

import jsep from 'jsep';
import { IPropertyBag } from 'app/extensibility/property-bag';

interface IIdentifier {
    type: 'Identifier';
    name: string;
}

interface IMemberExpression {
    type: 'MemberExpression';
    computed: boolean;
    object: Expression;
    property: Expression;
}

interface ILiteral {
    type: 'Literal';
    value: any;
    raw: string;
}

interface IThisExpression {
    type: 'ThisExpression';
}

interface ICallExpression {
    type: 'CallExpression';
    arguments: Expression[];
    callee: Expression;
}

interface IUnaryExpression {
    type: 'UnaryExpression';
    operator: '-' | '!' | '~' | '+';
    argument: Expression;
    prefix: boolean;
}

interface IBinaryExpression {
    type: 'BinaryExpression';
    operator: '|' | '^' | '&' | '==' | '!=' | '===' | '!==' | '<' | '>' | '<=' | '>=' | '<<' | '>>' | '>>>' | '+' | '-' | '*' | '/' | '%';
    left: Expression;
    right: Expression;
}

interface ILogicalExpression {
    type: 'LogicalExpression';
    operator: '||' | '&&';
    left: Expression;
    right: Expression;
}

interface IConditionalExpression {
    type: 'ConditionalExpression';
    test: Expression;
    consequent: Expression;
    alternate: Expression;
}

interface IArrayExpression {
    type: 'ArrayExpression';
    elements: Expression[];
}

type Expression = IIdentifier | IMemberExpression | ILiteral | IThisExpression | ICallExpression |
    IUnaryExpression | IBinaryExpression | ILogicalExpression | IConditionalExpression | IArrayExpression;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isExpressionString(expr: any): expr is string {
    if (typeof expr === 'string' && expr[0] === '{' && expr[expr.length - 1] === '}') {
        return true;
    } else {
        return false;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function calcExpressionString(expr: string, propertyBag: IPropertyBag): any {
    if (!isExpressionString(expr)) {
        throw new Error(`${expr} is not an expression.`);
    }

    return calcExpression(jsep(expr.substring(1, expr.length - 1)) as Expression, propertyBag);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function calcExpression(expr: Expression, propertyBag: IPropertyBag): any {
    switch (expr.type) {
        case 'Identifier':
            return propertyBag[expr.name];
        case 'MemberExpression':
            const obj = calcExpression(expr.object, propertyBag);
            return calcExpression(expr.property, obj);
        case 'Literal':
            return expr.value;
        case 'UnaryExpression':
            const argument = calcExpression(expr.argument, propertyBag);
            switch (expr.operator) {
                case '-':
                    return -argument;
                case '!':
                    return !argument;
                case '+':
                    return +argument;
                default:
                    throw new Error('Not Supported Expression.');
            }
        case 'BinaryExpression':
        case 'LogicalExpression':
            const left = calcExpression(expr.left, propertyBag);
            let right: any;
            if ((expr.operator !== '&&') && (expr.operator !== '||')) {
                // Do proper short-circuit validation in case of && and || 
                right = calcExpression(expr.right, propertyBag);
            }
            switch (expr.operator) {
                case '&&':
                    return left && calcExpression(expr.right, propertyBag);
                case '||':
                    return left || calcExpression(expr.right, propertyBag);
                case '==':
                    return left == right;
                case '!=':
                    return left != right;
                case '===':
                    return left === right;
                case '!==':
                    return left !== right;
                case '<':
                    return left < right;
                case '>':
                    return left > right;
                case '<=':
                    return left <= right;
                case '>=':
                    return left >= right;
                case '+':
                    return left + right;
                case '-':
                    return left - right;
                case '*':
                    return left * right;
                case '/':
                    return left / right;
                case '%':
                    return left % right;
                default:
                    throw new Error('Not Supported Expression.');
            }
        case 'ConditionalExpression':
            return calcExpression(expr.test, propertyBag) ? calcExpression(expr.consequent, propertyBag) : calcExpression(expr.alternate, propertyBag);
        case 'ThisExpression':
        case 'CallExpression':
        case 'ArrayExpression':
        default:
            throw new Error('Not Supported Expression.');
    }
}
